import React from 'react';
import { useWalkthrough } from './WalkthroughProvider';

interface TooltipPosition {
  top: number;
  left: number;
}

const WalkthroughTooltip: React.FC<{ targetRect: DOMRect }> = ({ targetRect }) => {
  const { currentStep, currentStepIndex, totalSteps, nextStep, prevStep, skipWalkthrough } = useWalkthrough();

  if (!currentStep) return null;

  const pos = currentStep.position || 'bottom';
  const tooltipWidth = 320;
  const gap = 12;

  const getPosition = (): TooltipPosition => {
    switch (pos) {
      case 'top':
        return {
          top: targetRect.top - gap,
          left: targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
        };
      case 'left':
        return {
          top: targetRect.top + targetRect.height / 2 - 80,
          left: targetRect.left - tooltipWidth - gap,
        };
      case 'right':
        return {
          top: targetRect.top + targetRect.height / 2 - 80,
          left: targetRect.right + gap,
        };
      case 'bottom':
      default:
        return {
          top: targetRect.bottom + gap,
          left: targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
        };
    }
  };

  const { top, left } = getPosition();

  // Clamp to viewport
  const clampedLeft = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
  const transformOrigin = pos === 'top' ? 'bottom center'
    : pos === 'bottom' ? 'top center'
    : pos === 'left' ? 'center right'
    : 'center left';

  return (
    <div style={{
      position: 'fixed',
      top: pos === 'top' ? 'auto' : `${top}px`,
      bottom: pos === 'top' ? `${window.innerHeight - top}px` : 'auto',
      left: `${clampedLeft}px`,
      width: `${tooltipWidth}px`,
      padding: '20px',
      borderRadius: '10px',
      backgroundColor: 'white',
      boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 9002,
      transformOrigin,
      animation: 'tooltipFadeIn 0.2s ease',
    }}>
      <style>{`
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Step counter */}
      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>
        Step {currentStepIndex + 1} of {totalSteps}
      </div>

      {/* Title */}
      <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', margin: '0 0 8px 0' }}>
        {currentStep.title}
      </h4>

      {/* Description */}
      <p style={{ fontSize: '14px', color: '#4b5563', margin: '0 0 20px 0', lineHeight: 1.5 }}>
        {currentStep.description}
      </p>

      {/* Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          onClick={skipWalkthrough}
          style={{
            background: 'none', border: 'none', color: '#9ca3af',
            fontSize: '13px', cursor: 'pointer', padding: '4px 0',
          }}
        >
          Skip Tutorial
        </button>
        <div style={{ display: 'flex', gap: '8px' }}>
          {currentStepIndex > 0 && (
            <button
              onClick={prevStep}
              style={{
                padding: '8px 16px', backgroundColor: '#F3F4F6', color: '#374151',
                border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}
            >
              Back
            </button>
          )}
          <button
            onClick={nextStep}
            style={{
              padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white',
              border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {currentStepIndex === totalSteps - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalkthroughTooltip;
